import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  BarChart3, 
  TrendingUp, 
  Bell,
  Radio,
  Clock,
  Hash,
  Mail,
  Smartphone,
  Zap,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple bar chart component
function SimpleBarChart({ data, maxValue, color = 'bg-primary' }) {
  if (!data || data.length === 0) return null;
  const max = maxValue || Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((item, index) => (
        <div key={`${item.day || item.hour || 'bar'}-${index}`} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full flex flex-col justify-end h-24">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${(item.count / max) * 100}%` }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`w-full ${color} rounded-t-sm min-h-[2px]`}
              title={`${item.count} alerts`}
            />
          </div>
          <span className="text-[10px] text-muted-foreground">{item.day || item.hour}</span>
        </div>
      ))}
    </div>
  );
}

// Horizontal bar for top items
function HorizontalBar({ label, count, maxCount, color = 'bg-primary' }) {
  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="truncate flex-1 mr-2">{label}</span>
        <span className="font-medium text-muted-foreground">{count}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5 }}
          className={`h-full ${color} rounded-full`}
        />
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/analytics`, { withCredentials: true });
        setAnalytics(response.data);
      } catch (error) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const summary = analytics?.summary || {};
  const maxChannelCount = analytics?.top_channels?.[0]?.count || 1;
  const maxKeywordCount = analytics?.top_keywords?.[0]?.count || 1;

  const getDeliveryIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'email': return Mail;
      case 'sms': return Smartphone;
      case 'push': return Bell;
      default: return Zap;
    }
  };

  const statCards = [
    { title: 'Total Alerts', value: summary.total_alerts || 0, icon: Bell, color: 'text-primary', bgColor: 'bg-primary/10' },
    { title: 'Today', value: summary.alerts_today || 0, icon: Calendar, color: 'text-accent', bgColor: 'bg-accent/10' },
    { title: 'This Week', value: summary.alerts_this_week || 0, icon: TrendingUp, color: 'text-success', bgColor: 'bg-success/10' },
    { title: 'This Month', value: summary.alerts_this_month || 0, icon: BarChart3, color: 'text-warning', bgColor: 'bg-warning/10' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight">
            Alert Analytics
          </h1>
          <p className="text-muted-foreground mt-1">
            Track your alert patterns and performance
          </p>
        </motion.div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-border/50">
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs md:text-sm text-muted-foreground">{stat.title}</p>
                        <p className="text-2xl md:text-3xl font-bold font-['Outfit'] mt-1">{stat.value}</p>
                      </div>
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 md:w-6 md:h-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Alerts Over Time */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Alerts (Last 7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.alerts_by_day?.length > 0 ? (
                  <SimpleBarChart data={analytics.alerts_by_day} />
                ) : (
                  <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                    No alert data yet
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Avg. per day</span>
                  <span className="font-semibold">{summary.avg_alerts_per_day || 0}</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Alerts by Hour */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5 text-accent" />
                  Peak Hours (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.alerts_by_hour?.some(h => h.count > 0) ? (
                  <SimpleBarChart 
                    data={analytics.alerts_by_hour.map(h => ({ ...h, day: `${h.hour}` }))} 
                    color="bg-accent"
                  />
                ) : (
                  <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                    No hourly data yet
                  </div>
                )}
                <p className="mt-4 pt-4 border-t border-border/50 text-xs text-muted-foreground text-center">
                  Hours shown in UTC
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Details Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Top Channels */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="border-border/50 h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
                  <Radio className="w-5 h-5 text-primary" />
                  Top Channels
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.top_channels?.length > 0 ? (
                  <div className="space-y-4">
                    {analytics.top_channels.map((channel) => (
                      <HorizontalBar
                        key={channel.channel || channel.channel_id}
                        label={channel.channel}
                        count={channel.count}
                        maxCount={maxChannelCount}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">
                    No channel data yet
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Top Keywords */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="border-border/50 h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
                  <Hash className="w-5 h-5 text-accent" />
                  Top Keywords
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.top_keywords?.length > 0 ? (
                  <div className="space-y-4">
                    {analytics.top_keywords.slice(0, 5).map((keyword) => (
                      <HorizontalBar
                        key={keyword.keyword}
                        label={keyword.keyword}
                        count={keyword.count}
                        maxCount={maxKeywordCount}
                        color="bg-accent"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">
                    No keyword data yet
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Delivery Methods */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="border-border/50 h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-success" />
                  Delivery Methods
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.delivery_methods?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.delivery_methods.map((method) => {
                      const Icon = getDeliveryIcon(method.method);
                      const total = analytics.delivery_methods.reduce((sum, m) => sum + m.count, 0);
                      const percentage = total > 0 ? Math.round((method.count / total) * 100) : 0;
                      
                      return (
                        <div key={method.method || 'unknown'} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-success" />
                            </div>
                            <div>
                              <p className="font-medium capitalize">{method.method || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">{method.count} alerts</p>
                            </div>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {percentage}%
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">
                    No delivery data yet
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Active Monitors Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card className="border-border/50 bg-primary/5">
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Radio className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Active Monitors</p>
                    <p className="text-2xl font-bold font-['Outfit']">{summary.active_monitors || 0}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground text-center sm:text-right">
                  Monitoring channels 24/7 for keyword matches
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
